import React from 'react';
import styled from 'styled-components';
import { StatusType, theme } from 'styles';
import { CenterFlex } from '../Flex';

// Extended type for StatusBubble to support additional colors
export type StatusBubbleType = StatusType | 'neutral' | 'disabled';

const SBubble = styled.div<{ bgColor: string; color: string; borderColor: string }>`
  height: 30px;
  padding: 0 10px 0 10px;
  border-radius: 16px;
  background-color: ${({ bgColor }) => bgColor};
  color: ${({ color }) => color};
  border: ${({ borderColor }) => borderColor} solid 1px;
  white-space: nowrap;
  font-weight: bold;
`;

export interface BubbleProps {
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export const Bubble = ({ children, className, disabled }: BubbleProps) => {
  return (
    <SBubble
      className={className}
      bgColor={disabled ? theme.color.shadow_5 : theme.color.off_white}
      color={disabled ? theme.color.shadow_8 : theme.color.yellowish_orange}
      borderColor={disabled ? theme.color.shadow_6 : theme.color.yellow_light_light_120}
    >
      <CenterFlex>{children}</CenterFlex>
    </SBubble>
  );
};

interface StatusColorsType {
  bgColor: string;
  color: string;
  borderColor: string;
}

const getStatusColors = (type: StatusBubbleType): StatusColorsType => {
  switch (type) {
    case 'success':
      return {
        bgColor: theme.color.success_bg_light,
        color: theme.color.selected,
        borderColor: theme.color.success,
      };
    case 'warning':
      return {
        bgColor: theme.color.warning_bg_light,
        color: theme.color.warning,
        borderColor: theme.color.warning,
      };
    case 'error':
      return {
        bgColor: theme.color.error_bg_light,
        color: theme.color.error,
        borderColor: theme.color.error,
      };
    case 'neutral':
      return {
        bgColor: theme.color.palette_purple_1,
        color: theme.color.purple,
        borderColor: theme.color.purple,
      };
    case 'disabled':
      return {
        bgColor: theme.color.grey90,
        color: theme.color.grey50,
        borderColor: theme.color.grey70,
      };
    case 'info':
    default:
      return {
        bgColor: theme.color.info_blue_light,
        color: theme.color.info,
        borderColor: theme.color.info,
      };
  }
};

export const StatusBubble = ({ children, statusType }: { children: React.ReactNode; statusType: StatusBubbleType }) => {
  return (
    <SBubble
      bgColor={getStatusColors(statusType).bgColor}
      color={getStatusColors(statusType).color}
      borderColor={getStatusColors(statusType).borderColor}
    >
      <CenterFlex>{children}</CenterFlex>
    </SBubble>
  );
};
